import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Chip, Avatar, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, IconButton, Grid, FormControl, InputLabel,
  Select, MenuItem, Tooltip, Pagination
} from '@mui/material';
import {
  CheckCircle, Cancel, Timer, AlarmOff, LocationOn,
  LocationOff, AccessTime, Close, Event, EventAvailable,
  EventBusy, KeyboardArrowLeft, KeyboardArrowRight, Alarm,
  ArrowBack
} from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';
import { axiosInstance, localAxiosGet } from '../../../Axios/axios';
import { alpha } from '@mui/material/styles';
import { Loader } from '../../../components/loade';

const ViewAllAttendance = ({
  viewAll,
  selectedDate,
  statusFilter,
  accessToken,
  inOfficeCheckIn = '',
  inOfficeCheckOut = '',
  onInOfficeCheckInChange,
  onInOfficeCheckOutChange,
  onManagerSelect,
  onClearFilters,
  hasActiveFilters,

}) => {
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const organizationId = localStorage.getItem('organizationId');
  const [viewingManagerEmployees, setViewingManagerEmployees] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [managerEmployees, setManagerEmployees] = useState([]);
  const [managerEmployeesLoading, setManagerEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [monthlyAttendance, setMonthlyAttendance] = useState({});
  const [monthlyOverview, setMonthlyOverview] = useState({
    present: 0,
    absent: 0,
    late: 0,
    weekend: 0,
    leave: 0
  });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(dayjs().startOf('month'));
  const [selectedStatus, setSelectedStatus] = useState('');
  const [localStatusFilter, setLocalStatusFilter] = useState(statusFilter || '');
  const [calendarLoading, setCalendarLoading] = useState(false);
  const userId = localStorage.getItem('userId');
  const [apiPagination, setApiPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const [managerApiPagination, setManagerApiPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const [holidaysList, setHolidaysList] = useState([]);

  const fetchHolidays = async (calendarDate = selectedCalendarDate) => {
    const locationId = localStorage.getItem('locationId');
    const organizationId = localStorage.getItem('organizationId');
    const year = calendarDate.year();
    const month = calendarDate.month() + 1;

    console.log('Fetching holidays for:', { year, month, calendarDate: calendarDate.format('YYYY-MM') });

    try {
      const response = await axiosInstance(localAxiosGet(
        `/organization/getAllHolidaysByYearAndMonth?year=${year}&organization_id=${organizationId}&location_id=${locationId}&month=${month}`,
        accessToken
      ));
      if (response.data && response.data.message && response.data.message.holidays) {
        console.log('Holidays received:', response.data.message.holidays);
        setHolidaysList(response.data.message.holidays);
      } else {
        console.log('No holidays in response');
        setHolidaysList([]);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      setHolidaysList([]);
    }
  };

  const fetchAllEmployeesAttendance = async (date = selectedDate, pageNum = 1, limitNum = rowsPerPage) => {
    setLoading(true);
    try {
      const formattedDate = date.format('MM/DD/YYYY');

      // Use the parameters passed to the function, not the state values
      let url = `/organization/getAllEmployeesUnderManagerAttendance?Date=${formattedDate}&manager_id=${userId}&page=${pageNum}&limit=${limitNum}`;

      if (inOfficeCheckIn) {
        url += `&in_office_check_in=${inOfficeCheckIn}`;
      }

      if (inOfficeCheckOut) {
        url += `&in_office_check_out=${inOfficeCheckOut}`;
      }

      // Use statusFilter or localStatusFilter consistently
      if (statusFilter && statusFilter !== '') {
        url += `&status=${statusFilter}`;
      }

      const response = await axiosInstance(localAxiosGet(url, accessToken));

      if (response.data.status === 'SUCCESS') {
        setAllEmployees(response.data.message.data || []);

        // Update state with the actual values used in the API call
        setApiPagination({
          page: pageNum, // Use the parameter, not response data
          limit: limitNum, // Use the parameter, not response data
          total: response.data.message.total || 0,
          totalPages: response.data.message.totalPages || 1
        });

        if (response.data.message.summary && onManagerSelect) {
          onManagerSelect(userId, response.data.message.summary);
        }
      } else {
        console.error('Failed to fetch attendance data:', response.data);
        setAllEmployees([]);
        setApiPagination({ page: 1, limit: limitNum, total: 0, totalPages: 1 });
      }
    } catch (error) {
      console.error('Error fetching all employees attendance:', error);
      setAllEmployees([]);
      setApiPagination({ page: 1, limit: limitNum, total: 0, totalPages: 1 });
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };



  const handleStatusFilterChange = (newStatus) => {
    setLocalStatusFilter(newStatus);

    // Reset pagination to page 1 when filter changes
    if (viewingManagerEmployees) {
      // First update the state, then call the API
      setManagerApiPagination(prev => ({ ...prev, page: 1 }));
      if (selectedManager) {
        // Pass explicit values, don't rely on state
        fetchManagerEmployees(selectedManager.user_id, selectedDate, 1, managerApiPagination.limit);
      }
    } else {
      // First update the state, then call the API
      setApiPagination(prev => ({ ...prev, page: 1 }));
      // Pass explicit values, don't rely on state
      fetchAllEmployeesAttendance(selectedDate, 1, apiPagination.limit);
    }
  };

  const fetchManagerEmployees = async (managerId, date = selectedDate, pageNum = 1, limitNum = 10) => {
    setManagerEmployeesLoading(true);
    setLoading(true);
    try {
      const formattedDate = date.isValid() ? date.format('MM/DD/YYYY') : dayjs().format('MM/DD/YYYY');

      // Use the parameters passed to the function, not the state values
      let url = `/organization/getAllEmployeesUnderManagerAttendance?Date=${formattedDate}&manager_id=${managerId}&page=${pageNum}&limit=${limitNum}`;

      if (inOfficeCheckIn) {
        url += `&in_office_check_in=${inOfficeCheckIn}`;
      }

      if (inOfficeCheckOut) {
        url += `&in_office_check_out=${inOfficeCheckOut}`;
      }

      // Use statusFilter consistently
      if (statusFilter && statusFilter !== '') {
        url += `&status=${statusFilter}`;
      }

      const response = await axiosInstance(localAxiosGet(url, accessToken));

      if (response.data.status === 'SUCCESS') {
        setManagerEmployees(response.data.message.data || []);

        // Update state with the actual values used in the API call
        setManagerApiPagination({
          page: pageNum, // Use the parameter, not response data
          limit: limitNum, // Use the parameter, not response data
          total: response.data.message.total || 0,
          totalPages: response.data.message.totalPages || 1
        });

        if (response.data.message.summary && onManagerSelect) {
          onManagerSelect(managerId, response.data.message.summary);
        }
      } else {
        console.error('Failed to fetch manager employees data:', response.data);
        setManagerEmployees([]);
        setManagerApiPagination({ page: 1, limit: limitNum, total: 0, totalPages: 1 });
      }
    } catch (error) {
      console.error('Error fetching manager employees:', error);
      setManagerEmployees([]);
      setManagerApiPagination({ page: 1, limit: limitNum, total: 0, totalPages: 1 });
    } finally {
      setManagerEmployeesLoading(false);
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };
  const handleViewAllAttendanceUnderManager = (employee) => {
    setSelectedManager(employee);
    setViewingManagerEmployees(true);
    setPage(0);
    setManagerApiPagination({ page: 1, limit: 10, total: 0, totalPages: 1 });

    fetchManagerEmployees(employee.user_id, selectedDate, 1, 10);

    if (onManagerSelect) {
      onManagerSelect(employee.user_id);
    }
  };

  const handleBackToAllEmployees = () => {
    setViewingManagerEmployees(false);
    setSelectedManager(null);
    setManagerEmployees([]);
    setPage(0);
    setManagerApiPagination({ page: 1, limit: 10, total: 0, totalPages: 1 });


    if (onManagerSelect) {
      onManagerSelect();
    }
  };


  const handleApiPageChange = (event, newPage) => {
    setApiPagination(prev => ({ ...prev, page: newPage }));
    fetchAllEmployeesAttendance(selectedDate, newPage, apiPagination.limit);
  };

  const handleManagerApiPageChange = (event, newPage) => {
    setManagerApiPagination(prev => ({ ...prev, page: newPage }));
    if (selectedManager) {
      fetchManagerEmployees(selectedManager.user_id, selectedDate, newPage, managerApiPagination.limit);
    }
  };

  useEffect(() => {
    if (viewAll) {
      fetchAllEmployeesAttendance(selectedDate, 1, 10);
    }
  }, [viewAll, organizationId]);

  useEffect(() => {
    if (viewAll && selectedDate) {
      if (viewingManagerEmployees && selectedManager) {
        setManagerApiPagination(prev => ({ ...prev, page: 1 }));
        fetchManagerEmployees(selectedManager.user_id, selectedDate, 1, managerApiPagination.limit);
      } else {
        setApiPagination(prev => ({ ...prev, page: 1 }));
        fetchAllEmployeesAttendance(selectedDate, 1, apiPagination.limit);
      }
    }
  }, [selectedDate, viewAll, inOfficeCheckIn, inOfficeCheckOut, statusFilter]);


  useEffect(() => {
    if (!hasActiveFilters) {
      if (viewingManagerEmployees && selectedManager) {
        fetchManagerEmployees(selectedManager.user_id, dayjs(), 1, managerApiPagination.limit);
      } else {
        fetchAllEmployeesAttendance(dayjs(), 1, apiPagination.limit);
      }
    }
  }, [hasActiveFilters]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'present':
        return '#4caf50';
      case 'absent':
        return '#f44336';
      case 'latearrival':
      case 'late':
        return '#ff9800';
      case 'leave':
        return '#2196f3';
      case 'weekend':
        return '#9e9e9e';
      default:
        return '#9e9e9e';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'present':
        return <CheckCircle fontSize="small" />;
      case 'absent':
        return <Cancel fontSize="small" />;
      case 'latearrival':
      case 'late':
        return <Timer fontSize="small" />;
      case 'leave':
        return <AlarmOff fontSize="small" />;
      case 'weekend':
        return <Event fontSize="small" />;
      default:
        return <Cancel fontSize="small" />;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '--:--';
    return dayjs(timeString).format('HH:mm');
  };

  const formatStatus = (status) => {
    if (!status) return 'No Record';
    if (status === 'LateArrival') {
      return 'Late Arrival';
    }
    return status;
  };

  const currentEmployees = viewingManagerEmployees ? managerEmployees : allEmployees;
  const currentLoading = viewingManagerEmployees ? managerEmployeesLoading : loading;
  const currentPagination = viewingManagerEmployees ? managerApiPagination : apiPagination;

  const filteredEmployees = currentEmployees.filter(employee => {
    if (statusFilter === '') return true;
    const employeeStatus = employee.status || 'absent';
    return employeeStatus.toLowerCase() === statusFilter.toLowerCase();
  });

  const getEmployeeId = (employee) => {
    return employee?.employee_id || employee?.user_id || employee?.id;
  };



  const handleEmployeeClick = (employee) => {

    setSelectedEmployee(employee);
    const initialDate = dayjs().startOf('month');
    setSelectedCalendarDate(initialDate);
    setOpenDialog(true);

    const employeeId = getEmployeeId(employee);
    if (employeeId) {
      // Pass the initial date explicitly
      fetchEmployeeMonthlyAttendance(employeeId, initialDate.format('MMMM'), initialDate);
    } else {
      console.error("Employee ID is undefined", employee);
    }
  };


  const handleCalendarDateChange = (newDate) => {
    const newMonthDate = newDate.startOf('month');
    console.log('Calendar date changing to:', newMonthDate.format('YYYY-MM-DD'));

    setSelectedCalendarDate(newMonthDate);

    if (!selectedEmployee) {
      console.error("No employee selected");
      return;
    }

    const employeeId = getEmployeeId(selectedEmployee);
    if (!employeeId) {
      console.error("Employee ID missing in:", selectedEmployee);
      return;
    }

    // Pass the newMonthDate to the function to avoid closure issues
    fetchEmployeeMonthlyAttendance(employeeId, newMonthDate.format('MMMM'), newMonthDate);
    fetchHolidays(newMonthDate); // Also fetch holidays for the new month
  };



  const handleStatusChange = (newStatus) => {
    const newMonthDate = selectedDate.startOf('month');
    setSelectedStatus(newStatus);
    const employeeId = getEmployeeId(selectedEmployee);
    if (!employeeId) {
      console.error("Employee ID missing in:", selectedEmployee);
      return;
    }
    fetchEmployeeMonthlyAttendance(employeeId, newMonthDate.format('MMMM'), newMonthDate);
  };

  const fetchEmployeeMonthlyAttendance = async (employeeId, monthYearString, calendarDate) => {
    setCalendarLoading(true);
    try {
      setMonthlyAttendance({});
      setMonthlyOverview({ present: 0, absent: 0, late: 0, weekend: 0, leave: 0 });

      console.log('Fetching attendance for:', {
        employeeId,
        monthYearString,
        calendarDate: calendarDate?.format('YYYY-MM') || selectedCalendarDate.format('YYYY-MM')
      });

      const response = await axiosInstance(localAxiosGet(
        `/organization/getAttendanceByEmployeeIdByStatus?employee_id=${employeeId}&month=${monthYearString}`,
        accessToken
      ));

      // Pass the correct calendar date to fetchHolidays
      const dateToUse = calendarDate || selectedCalendarDate;
      await fetchHolidays(dateToUse);

      const transformedData = {};
      let presentCount = 0;
      let absentCount = 0;
      let lateCount = 0;
      let weekendCount = 0;
      let leaveCount = 0;

      if (response.data && response.data.message && response.data.message.attendanceRecords) {
        response.data.message.attendanceRecords.forEach(item => {
          const day = dayjs(item.date).format('D');
          const dayDate = dayjs(item.date);
          const isWeekend = dayDate.day() === 0 || dayDate.day() === 6;

          transformedData[day] = {
            date: item.date,
            inTime: item.check_in ? dayjs(item.check_in).format('HH:mm') : '',
            outTime: item.check_out ? dayjs(item.check_out).format('HH:mm') : '',
            status: item.status,
            inOffice: item.office_premises?.check_in?.in_office === 'yes',
            outOffice: item.office_premises?.check_out?.in_office === 'yes',
            checkInIp: item.office_premises?.check_in?.ip,
            checkOutIp: item.office_premises?.check_out?.ip,
          };

          if (item.status === 'Present') {
            presentCount++;
          } else if (item.status === 'Absent') {
            absentCount++;
          } else if (item.status === 'LateArrival') {
            lateCount++;
          } else if (item.status === 'Leave') {
            leaveCount++;
          }

          if (isWeekend) {
            weekendCount++;
          }
        });

        setMonthlyAttendance(transformedData);
        setMonthlyOverview({
          present: presentCount,
          absent: absentCount,
          late: lateCount,
          weekend: weekendCount,
          leave: leaveCount
        });
      }
    } catch (error) {
      console.error("Error fetching employee monthly attendance data:", error);
      setMonthlyAttendance({});
      setMonthlyOverview({ present: 0, absent: 0, late: 0, weekend: 0, leave: 0 });
    } finally {
      setCalendarLoading(false);
    }
  };

  const statusOptions = [
    { value: '', label: 'All' },
    { value: 'Present', label: 'Present' },
    { value: 'Absent', label: 'Absent' },
    { value: 'LateArrival', label: 'Late' },
    { value: 'Leave', label: 'Leave' },
    { value: 'Holiday', label: 'Holiday' },
  ];

  const selectedMonthFormatted = selectedCalendarDate.format('MMMM');

  if (!viewAll) {
    return null;
  }

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);

    if (viewingManagerEmployees) {
      setManagerApiPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
      if (selectedManager) {
        fetchManagerEmployees(selectedManager.user_id, selectedDate, 1, newLimit);
      }
    } else {
      setApiPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
      fetchAllEmployeesAttendance(selectedDate, 1, newLimit);
    }
  };


  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      {loading && (<Loader />)}
      <TableContainer component={Paper} sx={{
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        overflow: 'hidden'
      }}>
        {viewingManagerEmployees && selectedManager && (
          <Box sx={{
            p: 2,
            bgcolor: alpha('#1a237e', 0.05),
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToAllEmployees}
              variant="outlined"
              size="small"
              sx={{
                borderColor: '#1a237e',
                color: '#1a237e',
                '&:hover': {
                  bgcolor: alpha('#1a237e', 0.1)
                }
              }}
            >
              Back to All Employees
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: '#1a237e',
                  width: 32,
                  height: 32,
                  fontSize: '0.8rem'
                }}
              >
                {selectedManager.name ? selectedManager.name.charAt(0).toUpperCase() : ''}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  Employees under {selectedManager.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentPagination.total} employee{currentPagination.total !== 1 ? 's' : ''} found
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        {currentLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table sx={{ minWidth: 650 }} aria-label="all employees attendance table">
              <TableHead sx={{
                background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Employee Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Check-in</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Check-out</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>In Office Check-in</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>In Office Check-out</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Production Hours</TableCell>
                  {!viewingManagerEmployees && (
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentEmployees.length > 0 ? (
                  currentEmployees.map((employee, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha('#1a237e', 0.04),
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              bgcolor: viewingManagerEmployees ? '#2196f3' : '#1a237e',
                              width: 36,
                              height: 36,
                              fontSize: '0.9rem'
                            }}
                            onClick={() => handleEmployeeClick(employee)}
                          >
                            {employee.name ? employee.name.charAt(0).toUpperCase() : ''}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{employee.name}</Typography>
                            {viewingManagerEmployees && (
                              <Typography variant="caption" color="text.secondary">
                                Under {selectedManager.name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={getStatusIcon(employee.status)}
                          label={formatStatus(employee.status)}
                          sx={{
                            bgcolor: `${getStatusColor(employee.status)}15`,
                            color: getStatusColor(employee.status),
                            fontWeight: 'medium'
                          }}
                        />
                      </TableCell>
                      <TableCell>{formatTime(employee.check_in)}</TableCell>
                      <TableCell>{formatTime(employee.check_out)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {employee.office_premises?.check_in?.in_office === 'yes' ? (
                            <Tooltip title={`IP: ${employee.office_premises?.check_in?.ip || 'N/A'}`} arrow>
                              <LocationOn fontSize="small" color="success" />
                            </Tooltip>
                          ) : (
                            <Tooltip title={`IP: ${employee.office_premises?.check_in?.ip || 'N/A'}`} arrow>
                              <LocationOff fontSize="small" color="error" />
                            </Tooltip>
                          )}
                          <Typography variant="body2">
                            {employee.office_premises?.check_in?.in_office === 'yes' ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {employee.office_premises?.check_out?.in_office === 'yes' ? (
                            <Tooltip title={`IP: ${employee.office_premises?.check_out?.ip || 'N/A'}`} arrow>
                              <LocationOn fontSize="small" color="success" />
                            </Tooltip>
                          ) : (
                            <Tooltip title={`IP: ${employee.office_premises?.check_out?.ip || 'N/A'}`} arrow>
                              <LocationOff fontSize="small" color="error" />
                            </Tooltip>
                          )}
                          <Typography variant="body2">
                            {employee.office_premises?.check_out?.in_office === 'yes' ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTime fontSize="small" color="primary" />
                          <Typography variant="body2">
                            {employee.total_hours || '--:--'}
                          </Typography>
                        </Box>
                      </TableCell>
                      {!viewingManagerEmployees && (
                        <TableCell>
                          {['Manager', 'Team Lead'].includes(employee.user_type)   && (
                            <Button
                              onClick={() => handleViewAllAttendanceUnderManager(employee)}
                              variant="outlined"
                              size="small"
                              sx={{
                                fontSize: '0.75rem',
                                px: 2,
                                py: 0.5,
                                borderColor: '#1a237e',
                                color: '#1a237e',
                                '&:hover': {
                                  bgcolor: alpha('#1a237e', 0.1),
                                  borderColor: '#1a237e'
                                }
                              }}
                            >
                              View Team
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={viewingManagerEmployees ? 7 : 8} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        {viewingManagerEmployees
                          ? `No employees found under ${selectedManager?.name || 'this manager'} for the selected date and filters`
                          : 'No attendance records found for the selected date and filters'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              p: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min(currentPagination.limit * (currentPagination.page - 1) + 1, currentPagination.total)} - {Math.min(currentPagination.limit * currentPagination.page, currentPagination.total)} of {currentPagination.total} employees
                </Typography>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Rows per page</InputLabel>
                  <Select
                    value={currentPagination.limit}
                    label="Rows per page"
                    onChange={(event) => handleChangeRowsPerPage(event)}
                  >
                    {/* <MenuItem value={2}>2</MenuItem> */}
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Pagination
                count={currentPagination.totalPages}
                page={currentPagination.page}
                onChange={viewingManagerEmployees ? handleManagerApiPageChange : handleApiPageChange}
                color="primary"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 1,
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#1a237e !important',
                    color: 'white',
                  }
                }}
              />
            </Box>
          </>
        )}
      </TableContainer>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        {selectedEmployee && (
          <>
            <DialogTitle sx={{
              background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    width: 50,
                    height: 50
                  }}
                >
                  {selectedEmployee.name ? selectedEmployee.name.charAt(0).toUpperCase() : ''}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedEmployee.name}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {selectedEmployee.designation || 'Employee'}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => setOpenDialog(false)}
                sx={{ color: 'white' }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              {calendarLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Box sx={{ mb: 4, mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#1a237e', fontWeight: 'bold' }}>
                      Monthly Overview - {selectedMonthFormatted}
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{
                          p: 2,
                          textAlign: 'center',
                          background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                          color: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)'
                        }}>
                          <CheckCircle sx={{ color: 'white', fontSize: 32, mb: 1, opacity: 0.9 }} />
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {monthlyOverview.present}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            Present Days
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{
                          p: 2,
                          textAlign: 'center',
                          background: 'linear-gradient(135deg, #f44336 0%, #e57373 100%)',
                          color: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)'
                        }}>
                          <Cancel sx={{ color: 'white', fontSize: 32, mb: 1, opacity: 0.9 }} />
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {monthlyOverview.absent}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            Absent Days
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{
                          p: 2,
                          textAlign: 'center',
                          background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                          color: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(255, 152, 0, 0.2)'
                        }}>
                          <Timer sx={{ color: 'white', fontSize: 32, mb: 1, opacity: 0.9 }} />
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {monthlyOverview.late}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            Late Days
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper sx={{
                          p: 2,
                          textAlign: 'center',
                          background: 'linear-gradient(135deg, #9c27b0 0%, #ab47bc 100%)',
                          color: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(156, 39, 176, 0.2)'
                        }}>
                          <AlarmOff sx={{ color: 'white', fontSize: 32, mb: 1, opacity: 0.9 }} />
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {monthlyOverview.leave || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                            Leave Days
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Event color="primary" sx={{ mr: 1.5 }} />
                        <Typography variant="subtitle1" fontWeight="medium">
                          Monthly Calendar View
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControl variant="outlined" sx={{ minWidth: 200, mr: 2 }}>
                          <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                          <Select
                            labelId="status-filter-label"
                            id="status-filter"
                            value={selectedStatus}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            label="Filter by Status"
                          >
                            {statusOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">
                          {selectedMonthFormatted} {selectedCalendarDate.year()}
                        </Typography>

                        <IconButton
                          size="small"
                          onClick={() => handleCalendarDateChange(selectedCalendarDate.subtract(1, 'month'))}
                          sx={{
                            bgcolor: alpha('#1a237e', 0.1),
                            '&:hover': { bgcolor: alpha('#1a237e', 0.2) },
                            '&.Mui-disabled': { opacity: 0.5 }
                          }}
                        >
                          <KeyboardArrowLeft fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleCalendarDateChange(selectedCalendarDate.add(1, 'month'))}
                          disabled={selectedCalendarDate.month() === 11 && selectedCalendarDate.year() === dayjs().year()}
                          sx={{
                            bgcolor: alpha('#1a237e', 0.1),
                            '&:hover': { bgcolor: alpha('#1a237e', 0.2) },
                            '&.Mui-disabled': { opacity: 0.5 }
                          }}
                        >
                          <KeyboardArrowRight fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        borderRadius: 2,
                        background: '#ffffff',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                      }}
                    >
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <Grid item xs={12 / 7} key={day}>
                            <Box
                              sx={{
                                textAlign: 'center',
                                fontWeight: 'bold',
                                p: 1,
                                color: day === 'Sun' || day === 'Sat' ? 'text.secondary' : 'text.primary',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
                              }}
                            >
                              {day}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                      <Grid container spacing={1}>
                        {(() => {
                          const firstDayOfMonth = selectedDate.startOf('month');
                          const daysInMonth = selectedDate.daysInMonth();
                          const startingDayOfWeek = firstDayOfMonth.day();
                          const calendarCells = [];

                          // Empty cells for days before the 1st of the month
                          for (let i = 0; i < startingDayOfWeek; i++) {
                            calendarCells.push(
                              <Grid item xs={12 / 7} key={`empty-${i}`}>
                                <Box
                                  sx={{
                                    height: 100,
                                    opacity: 0.3,
                                    bgcolor: alpha('#f5f5f5', 0.3),
                                    borderRadius: 1
                                  }}
                                />
                              </Grid>
                            );
                          }

                          // Days of the month
                          for (let day = 1; day <= daysInMonth; day++) {
                            const currentDate = selectedCalendarDate.date(day); // Use selectedCalendarDate instead of selectedDate
                            const dateString = currentDate.format('D');
                            const dayRecord = monthlyAttendance[dateString];

                            // Fix holiday detection logic
                            const isDayHoliday = holidaysList?.some(holiday => {
                              const holidayDate = dayjs(holiday.date, 'YYYY-MM-DD'); // Parse the API date format
                              return (
                                holidayDate.date() === day &&
                                holidayDate.month() === selectedCalendarDate.month() && // Use selectedCalendarDate
                                holidayDate.year() === selectedCalendarDate.year()
                              );
                            });

                            const holidayInfo = isDayHoliday
                              ? holidaysList.find(h => {
                                const hDate = dayjs(h.date, 'YYYY-MM-DD'); // Parse the API date format
                                return (
                                  hDate.date() === day &&
                                  hDate.month() === selectedCalendarDate.month() && // Use selectedCalendarDate
                                  hDate.year() === selectedCalendarDate.year()
                                );
                              })
                              : null;

                            // Fix status filtering to include holidays
                            if (selectedStatus) {
                              const shouldShow = (
                                (dayRecord && dayRecord.status?.toLowerCase() === selectedStatus.toLowerCase()) ||
                                (selectedStatus.toLowerCase() === 'holiday' && isDayHoliday)
                              );

                              if (!shouldShow) {
                                calendarCells.push(
                                  <Grid item xs={12 / 7} key={`day-${day}`}>
                                    <Box
                                      sx={{
                                        minHeight: { xs: 80, sm: 100 },
                                        opacity: 0.3,
                                        bgcolor: alpha('#f5f5f5', 0.3),
                                        borderRadius: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Typography variant="body2">{day}</Typography>
                                    </Box>
                                  </Grid>
                                );
                                continue;
                              }
                            }

                            // Determine status and color - prioritize holidays over attendance data
                            let status, statusColor, statusLabel;

                            if (isDayHoliday) {
                              status = 'Holiday';
                              statusColor = '#9c27b0';
                              statusLabel = 'Holiday';
                            } else if (dayRecord?.status) {
                              status = dayRecord.status;
                              statusColor = getStatusColor(status);
                              statusLabel = formatStatus(status);
                            } else {
                              status = null;
                              statusColor = null;
                              statusLabel = null;
                            }

                            calendarCells.push(
                              <Grid item xs={12 / 7} key={`day-${day}`}>
                                <Tooltip
                                  title={isDayHoliday
                                    ? `Holiday: ${holidayInfo?.holiday_name || 'Holiday'}`
                                    : dayRecord
                                      ? `${statusLabel}: In: ${dayRecord.inTime || '--:--'}, Out: ${dayRecord.outTime || '--:--'}`
                                      : ''
                                  }
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      minHeight: { xs: 80, sm: 100 },
                                      display: 'flex',
                                      flexDirection: 'column',
                                      borderRadius: 1,
                                      border: (dayRecord || isDayHoliday) ? `1px solid ${alpha(statusColor, 0.3)}` : '1px solid rgba(0, 0, 0, 0.12)',
                                      bgcolor: (dayRecord || isDayHoliday) ? alpha(statusColor, 0.1) : 'transparent',
                                      position: 'relative',
                                      cursor: (dayRecord || isDayHoliday) ? 'pointer' : 'default',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        transform: (dayRecord || isDayHoliday) ? 'translateY(-2px)' : 'none',
                                        boxShadow: (dayRecord || isDayHoliday) ? '0 4px 8px rgba(0,0,0,0.1)' : 'none',
                                        bgcolor: (dayRecord || isDayHoliday) ? alpha(statusColor, 0.2) : alpha('#f5f5f5', 0.1)
                                      },
                                    }}
                                  >
                                    <Box sx={{
                                      p: 1,
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      borderBottom: (dayRecord || isDayHoliday) ? `1px dashed ${alpha(statusColor, 0.3)}` : 'none'
                                    }}>
                                      <Typography
                                        variant="body2"
                                        fontWeight={day === dayjs().date() &&
                                          selectedCalendarDate.month() === dayjs().month() &&
                                          selectedCalendarDate.year() === dayjs().year() ? 'bold' : 'medium'}
                                        sx={{
                                          color: (dayRecord || isDayHoliday) ? statusColor : 'inherit',
                                          bgcolor: day === dayjs().date() &&
                                            selectedCalendarDate.month() === dayjs().month() &&
                                            selectedCalendarDate.year() === dayjs().year() ?
                                            alpha('#1a237e', 0.1) : 'transparent',
                                          borderRadius: '50%',
                                          width: 24,
                                          height: 24,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                      >
                                        {day}
                                      </Typography>

                                      {/* Show appropriate icon */}
                                      {isDayHoliday ? (
                                        <Event fontSize="small" sx={{ color: statusColor }} />
                                      ) : (
                                        dayRecord && status && (
                                          status.toLowerCase() === 'present' ? <CheckCircle fontSize="small" sx={{ color: statusColor }} /> :
                                            status.toLowerCase() === 'absent' ? <Cancel fontSize="small" sx={{ color: statusColor }} /> :
                                              status.toLowerCase() === 'latearrival' ? <Timer fontSize="small" sx={{ color: statusColor }} /> :
                                                status.toLowerCase() === 'leave' ? <AlarmOff fontSize="small" sx={{ color: statusColor }} /> :
                                                  <Event fontSize="small" sx={{ color: statusColor }} />
                                        )
                                      )}
                                    </Box>

                                    {/* Show chip for holidays or attendance status */}
                                    {(dayRecord || isDayHoliday) && (
                                      <>
                                        <Box sx={{ px: 1, pt: 1 }}>
                                          <Chip
                                            label={isDayHoliday
                                              ? (holidayInfo?.holiday_name || 'Holiday')
                                              : formatStatus(dayRecord.status)
                                            }
                                            size="small"
                                            sx={{
                                              height: 20,
                                              fontSize: '0.65rem',
                                              backgroundColor: alpha(statusColor, 0.2),
                                              color: statusColor,
                                              fontWeight: 'medium',
                                              '& .MuiChip-label': { px: 1 }
                                            }}
                                          />
                                        </Box>

                                        {/* Show time chips only for non-holiday days */}
                                        {dayRecord && !isDayHoliday && (
                                          <Box sx={{
                                            px: 1,
                                            mt: 'auto',
                                            pb: 1,
                                            display: 'flex',
                                            flexDirection: {
                                              '@media (max-width:1350px)': 'column',
                                              lg: 'row'
                                            },
                                            flexWrap: {
                                              xs: 'wrap',
                                              '@media (max-width:1350px)': 'nowrap',
                                            },
                                            alignItems: {
                                              xs: 'flex-start',
                                              sm: 'flex-start',
                                            },
                                            gap: {
                                              xs: 0.5,
                                              '@media (min-width:1360px) and (max-width:1399px)': 0,
                                              '@media (min-width:1600px)': 4,
                                            },
                                            justifyContent: {
                                              xs: 'flex-start',
                                              xl: 'space-between'
                                            },
                                            width: {
                                              xl: '90%',
                                              lg: '100%'
                                            }
                                          }}>
                                            {dayRecord.inTime && dayRecord.inTime !== '--:--' && (
                                              <Chip
                                                icon={
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {dayRecord.inOffice ?
                                                      <LocationOn sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, color: '#4caf50' }} /> :
                                                      <LocationOff sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, color: '#f44336' }} />
                                                    }
                                                    <Alarm sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, color: '#4caf50' }} />
                                                  </Box>
                                                }
                                                label={dayRecord.inTime}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                  height: { xs: 16, sm: 18 },
                                                  p: { xs: '1px', sm: '2px' },
                                                  fontSize: { xs: '0.55rem', sm: '0.65rem' },
                                                  backgroundColor: alpha('#4caf50', 0.1),
                                                  borderColor: alpha('#4caf50', 0.3),
                                                  color: '#4caf50',
                                                  '& .MuiChip-label': { px: { xs: 0.3, sm: 0.5 }, fontWeight: 'medium' },
                                                  '& .MuiChip-icon': { mr: { xs: 0, sm: 0.1 }, ml: { xs: 0.2, sm: 0 } },
                                                  '&:hover': {
                                                    backgroundColor: alpha('#4caf50', 0.15),
                                                    borderColor: alpha('#4caf50', 0.4)
                                                  }
                                                }}
                                              />
                                            )}

                                            {dayRecord.outTime && (
                                              <Chip
                                                icon={
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {dayRecord.outOffice ?
                                                      <LocationOn sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, color: '#4caf50' }} /> :
                                                      <LocationOff sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, color: '#f44336' }} />
                                                    }
                                                    <AlarmOff sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, color: '#f44336' }} />
                                                  </Box>
                                                }
                                                label={dayRecord.outTime}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                  height: { xs: 16, sm: 18 },
                                                  fontSize: { xs: '0.55rem', sm: '0.65rem' },
                                                  backgroundColor: alpha('#f44336', 0.1),
                                                  borderColor: alpha('#f44336', 0.3),
                                                  color: '#f44336',
                                                  '& .MuiChip-label': { px: { xs: 0.3, sm: 0.5 }, fontWeight: 'medium' },
                                                  '& .MuiChip-icon': { mr: { xs: 0, sm: 0.1 }, ml: { xs: 0.2, sm: 0.1 } },
                                                  '&:hover': {
                                                    backgroundColor: alpha('#f44336', 0.15),
                                                    borderColor: alpha('#f44336', 0.4)
                                                  }
                                                }}
                                              />
                                            )}
                                          </Box>
                                        )}
                                      </>
                                    )}
                                  </Box>
                                </Tooltip>
                              </Grid>
                            );
                          }

                          // Empty cells after the last day of the month
                          const totalCells = Math.ceil((daysInMonth + startingDayOfWeek) / 7) * 7;
                          const remainingCells = totalCells - (daysInMonth + startingDayOfWeek);
                          for (let i = 0; i < remainingCells; i++) {
                            calendarCells.push(
                              <Grid item xs={12 / 7} key={`empty-end-${i}`}>
                                <Box
                                  sx={{
                                    height: 100,
                                    opacity: 0.3,
                                    bgcolor: alpha('#f5f5f5', 0.3),
                                    borderRadius: 1
                                  }}
                                />
                              </Grid>
                            );
                          }

                          return calendarCells;
                        })()}
                      </Grid>
                      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50', mr: 1 }} />
                          <Typography variant="caption">Present</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336', mr: 1 }} />
                          <Typography variant="caption">Absent</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800', mr: 1 }} />
                          <Typography variant="caption">Late</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2196f3', mr: 1 }} />
                          <Typography variant="caption">Leave</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#9e9e9e', mr: 1 }} />
                          <Typography variant="caption">Weekend</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationOn sx={{ fontSize: '0.8rem', color: '#4caf50', mr: 0.5 }} />
                          <Typography variant="caption">In Office</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationOff sx={{ fontSize: '0.8rem', color: '#f44336', mr: 0.5 }} />
                          <Typography variant="caption">Remote</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, background: '#f5f5f5' }}>
              <Button
                onClick={() => setOpenDialog(false)}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0d47a1 0%, #1a237e 100%)'
                  }
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ViewAllAttendance;